import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  addPurchaseAccountingGroup,
  addSellAccountingGroup,
  fetchAllPurchaseAccountingGroups,
  fetchAllPurchaseVats,
  fetchAllSellAccountingGroups,
  fetchAllSellVats,
  generateMultiBookingReport,
  generatePreSellStats,
  generateSellStats,
  updatePurchaseAccountingGroup,
  updatePurchaseVat,
  updateSellAccountingGroup,
  updateSellVat,
} from '../../common/api/accountingService';

export interface AccountingGroupDetails {
  id: string;
  name: string;
  foreignTravelAgencyCommissionAccount: number;
  purchasesWithoutTaxesAccount: number;
  salesClaimAccount: number;
  salesVatDeptAccount: number;
  sellerCommissionAccount: number;
  salesAccount: number;
  purchaseVatDeptAccount: number;
  purchaseDeptAccount: number;
  discountAccount: number;
  purchaseAccount: number;
  advancePurchaseAccount: number;
  domesticTravelAgencyCommissionAccount: number;
  advanceSalesAccount: number;
  sellVatId: string;
  purchaseVatId: string;
}
export interface SellVatDetailsData {
  id: string;
  percentage: number;
  name: string;
  accountingGroup: string;
}

export interface PurchaseVatDetailsData {
  id: string;
  percentage: number;
  name: string;
  accountingGroup: string;
}

export interface AccountingsQueryFilters {
  culture: string;
  pageNumber: number;
  pageSize: number;
}

export interface SellStatsCommand {
  startDate: string;
  endDate: string;
  bookingNumber?: string;
}

export interface MultiBookingReportCommand {
  bookingContainerId?: string;
}

export interface PreSellStatsCommand {
  startDate: string;
  endDate: string;
  createdStartDate: string;
  createdEndDate: string;
  bookingNumber?: string;
}

export interface AccountingsState {
  sellVatsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  purchaseVatsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  sellAccountingGroupsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  purchaseAccountingGroupsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  sellVats: SellVatDetailsData[];
  purchaseVats: PurchaseVatDetailsData[];
  sellAccountingGroups: AccountingGroupDetails[];
  purchaseAccountingGroups: AccountingGroupDetails[];
}

const initialState: AccountingsState = {
  sellAccountingGroups: [],
  purchaseAccountingGroups: [],
  sellAccountingGroupsStatus: LOADING_STATUS.IDLE,
  purchaseAccountingGroupsStatus: LOADING_STATUS.IDLE,
  purchaseVats: [],
  sellVatsStatus: LOADING_STATUS.IDLE,
  purchaseVatsStatus: LOADING_STATUS.IDLE,
  sellVats: [],
};

export const updateSellAccountingGroupAsync = createAsyncThunk(
  'accountings/updateSellAccountingGroup',
  async (accountingGroupData: AccountingGroupDetails, { rejectWithValue }) => {
    try {
      const response = await updateSellAccountingGroup(accountingGroupData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updatePurchaseAccountingGroupAsync = createAsyncThunk(
  'accountings/updatePurchaseAccountingGroup',
  async (accountingGroupData: AccountingGroupDetails, { rejectWithValue }) => {
    try {
      const response = await updatePurchaseAccountingGroup(accountingGroupData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateSellVatAsync = createAsyncThunk(
  'accountings/updateSellVat',
  async (sellVatData: SellVatDetailsData, { rejectWithValue }) => {
    try {
      const response = await updateSellVat(sellVatData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const generateSellStatsAsync = createAsyncThunk(
  'accountings/generateSellStats',
  async (sellStatCommand: SellStatsCommand, { rejectWithValue }) => {
    try {
      const response = await generateSellStats(sellStatCommand);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const generateMultiBookingReportAsync = createAsyncThunk(
  'accountings/generateSellStats',
  async (sellStatCommand: MultiBookingReportCommand, { rejectWithValue }) => {
    try {
      const response = await generateMultiBookingReport(sellStatCommand);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const generatePreSellStatsAsync = createAsyncThunk(
  'accountings/generateSellStats',
  async (sellStatCommand: PreSellStatsCommand, { rejectWithValue }) => {
    try {
      const response = await generatePreSellStats(sellStatCommand);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updatePurchaseVatAsync = createAsyncThunk(
  'accountings/updatePurchaseVat',
  async (purchaseVatData: PurchaseVatDetailsData, { rejectWithValue }) => {
    try {
      const response = await updatePurchaseVat(purchaseVatData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addPurchaseAccountingGroupAsync = createAsyncThunk(
  'accountings/addPurchaseVat',
  async (purchaseVatData: AccountingGroupDetails, { rejectWithValue }) => {
    try {
      const response = await addPurchaseAccountingGroup(purchaseVatData);
      return response.message;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addSellAccountingGroupAsync = createAsyncThunk(
  'accountings/addSellVat',
  async (purchaseVatData: AccountingGroupDetails, { rejectWithValue }) => {
    try {
      const response = await addSellAccountingGroup(purchaseVatData);
      return response.message;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllSellVatsAsync = createAsyncThunk(
  'accountings/getallsellvats',
  async (filterCriteria: AccountingsQueryFilters, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllSellVats(filterCriteria);
      return response.data;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllPurchaseVatsAsync = createAsyncThunk(
  'accountings/getallpurchasevats',
  async (filterCriteria: AccountingsQueryFilters, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllPurchaseVats(filterCriteria);
      return response.data;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllSellAccountingGroupsAsync = createAsyncThunk(
  'accountings/getallsellaccountinggroups',
  async (filterCriteria: AccountingsQueryFilters, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllSellAccountingGroups(filterCriteria);
      return response.data;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAllPurchaselAccountingGroupsAsync = createAsyncThunk(
  'accountings/getallpurchaseaccountinggroups',
  async (filterCriteria: AccountingsQueryFilters, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllPurchaseAccountingGroups(filterCriteria);
      return response.data;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

const accountinglSice = createSlice({
  name: 'accountings',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      .addCase(getAllSellAccountingGroupsAsync.fulfilled, (state, action) => {
        state.sellAccountingGroupsStatus = LOADING_STATUS.IDLE;
        state.sellAccountingGroups = action.payload;
      })
      .addCase(getAllSellAccountingGroupsAsync.rejected, (state, action) => {
        state.sellAccountingGroupsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(
        getAllPurchaselAccountingGroupsAsync.fulfilled,
        (state, action) => {
          state.purchaseAccountingGroupsStatus = LOADING_STATUS.IDLE;
          state.purchaseAccountingGroups = action.payload;
        },
      )
      .addCase(
        getAllPurchaselAccountingGroupsAsync.rejected,
        (state, action) => {
          state.purchaseAccountingGroupsStatus = LOADING_STATUS.FAILED;
        },
      )
      .addCase(getAllSellVatsAsync.fulfilled, (state, action) => {
        state.sellVatsStatus = LOADING_STATUS.IDLE;
        state.sellVats = action.payload;
      })
      .addCase(getAllSellVatsAsync.rejected, (state, action) => {
        state.sellVatsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAllPurchaseVatsAsync.fulfilled, (state, action) => {
        state.purchaseVatsStatus = LOADING_STATUS.IDLE;
        state.purchaseVats = action.payload;
      })
      .addCase(getAllPurchaseVatsAsync.rejected, (state, action) => {
        state.purchaseVatsStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectSellVatsLoadingStatus = (state: RootState) =>
  state.accoounting.sellVatsStatus;
export const selectSellVats = (state: RootState) => state.accoounting.sellVats;
export const selectPurchaseVatsLoadingStatus = (state: RootState) =>
  state.accoounting.purchaseVatsStatus;
export const selectPurchaseVats = (state: RootState) =>
  state.accoounting.purchaseVats;
export const selectSellAccountingGroupsLoadingStatus = (state: RootState) =>
  state.accoounting.sellAccountingGroupsStatus;

export const selectPurchaseAccountingGroupsLoadingStatus = (state: RootState) =>
  state.accoounting.purchaseAccountingGroupsStatus;
export const selectSellAccountingGroups = (state: RootState) =>
  state.accoounting.sellAccountingGroups;

export const selectPurchaseAccountingGroups = (state: RootState) =>
  state.accoounting.purchaseAccountingGroups;
export default accountinglSice.reducer;
